import React, { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import Button from "../../Button";
import AffiliateApplicationForm from "./AffiliateApplicationForm";
import styles from "./AffiliateReferrals.module.css";

interface AffiliateData {
  user_id: string;
  status: "pending" | "active" | "inactive" | "rejected";
  default_commission_rate: number;
  approved_at?: string;
  notes?: string;
  payout_details?: any;
  created_at: string;
}

interface CommissionSummary {
  eligible_commission_minor: number;
  allocated_commission_minor: number;
  available_commission_minor: number;
  pending_commission_minor: number;
  processing_commission_minor: number;
  paid_commission_minor: number;
}

interface Payout {
  id: string;
  status: "pending" | "processing" | "paid" | "cancelled";
  payment_method?: string;
  payment_reference?: string;
  paid_at?: string;
  created_at: string;
  total_commission_minor: number;
}

interface AffiliateStatus {
  affiliate: AffiliateData | null;
  commission_summary: CommissionSummary | null;
  affiliate_coupon: { code: string } | null;
  payouts: Payout[];
}

interface AffiliateApplicationFormData {
  legal_name: string;
  business_name?: string;
  tax_id?: string;
  payout_method: "bank_transfer" | "paypal";
  // Bank Transfer fields
  bank_account_holder: string;
  bank_name: string;
  account_number: string;
  iban?: string;
  // PayPal field
  paypal_email: string;
  // Contact information
  address: string;
  city: string;
  country: string;
  postal_code: string;
  phone: string;
  notes?: string;
}

const AffiliateTab: React.FC = () => {
  const { user } = useAuth();
  const [affiliateStatus, setAffiliateStatus] =
    useState<AffiliateStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  // Currency state for displaying earnings based on user location (SAR for Saudi users, USD otherwise)
  const [currency, setCurrency] = useState<"SAR" | "USD">("SAR");

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    if (!user?.id) return;

    const fetchAffiliateStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/affiliate/status/${user.id}`
        );
        if (response.ok) {
          const data = await response.json();
          setAffiliateStatus(data);
        } else {
          console.error("Failed to fetch affiliate status");
        }
      } catch (error) {
        console.error("Error fetching affiliate status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliateStatus();
  }, [user?.id, API_URL]);

  // Fetch currency context similar to Pricing page so we display earnings in SAR or USD
  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing`);
        if (!res.ok) return; // If it fails, keep default SAR
        const json = await res.json();
        // Backend returns user's currency and pricing; use the currency value directly
        const detectedCurrency = (json?.currency || json?.pricing?.top_up?.currency || "SAR") as
          | "SAR"
          | "USD";
        setCurrency(detectedCurrency);
      } catch {
        // Ignore errors; default SAR remains
      }
    };
    fetchCurrency();
  }, [API_URL]);

  const handleRequestPayout = async () => {
    if (!user?.id) return;

    setRequestingPayout(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/request-payout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (response.ok) {
        (window as any).showNotification?.(
          "Payout request submitted successfully!",
          "success"
        );
        // Refresh affiliate status to show updated payout info
        const statusResponse = await fetch(
          `${API_URL}/api/affiliate/status/${user.id}`
        );
        if (statusResponse.ok) {
          const data = await statusResponse.json();
          setAffiliateStatus(data);
        }
      } else {
        const error = await response.json();
        (window as any).showNotification?.(
          error.error || "Failed to request payout.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error requesting payout:", error);
      (window as any).showNotification?.(
        "An unexpected error occurred.",
        "error"
      );
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleSubmitApplication = async (formData: AffiliateApplicationFormData) => {
    if (!user?.id) return;

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/affiliate/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          notes: formData.notes,
          payout_details: {
            legal_name: formData.legal_name,
            business_name: formData.business_name,
            tax_id: formData.tax_id,
            payout_method: formData.payout_method,
            // Bank transfer details (only if bank transfer is selected)
            ...(formData.payout_method === "bank_transfer" && {
              bank_account_holder: formData.bank_account_holder,
              bank_name: formData.bank_name,
              account_number: formData.account_number,
              iban: formData.iban,
            }),
            // PayPal details (only if PayPal is selected)
            ...(formData.payout_method === "paypal" && {
              paypal_email: formData.paypal_email,
            }),
            // Contact information (always included)
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postal_code: formData.postal_code,
            phone: formData.phone,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAffiliateStatus({
          affiliate: data,
          commission_summary: null,
          affiliate_coupon: null,
          payouts: [],
        });
        setShowApplicationForm(false);
        (window as any).showNotification?.(
          "Affiliate application submitted successfully!",
          "success"
        );
      } else {
        const error = await response.json();
        (window as any).showNotification?.(
          error.error || "Failed to submit application",
          "error"
        );
      }
    } catch (error) {
      console.error("Error submitting affiliate application:", error);
      (window as any).showNotification?.(
        "Failed to submit application",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (amountMinor: number): string => {
    // Commission values come from backend in minor SAR units (halala)
    const amountSar = amountMinor / 100; // Convert to SAR
    if (currency === "SAR") {
      // Display SAR with 2 decimals
      return `${amountSar.toFixed(2)} SAR`;
    }
    // For non-Saudi users, show USD. The backend uses a fixed rate: 1 USD = 3.75 SAR.
    const amountUsd = amountSar / 3.75;
    return `$${amountUsd.toFixed(2)}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "active":
        return "#28a745";
      case "pending":
        return "#ffc107";
      case "rejected":
        return "#dc3545";
      case "inactive":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case "active":
        return "Active";
      case "pending":
        return "Pending Review";
      case "rejected":
        return "Rejected";
      case "inactive":
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className={styles.affiliateTab}>
        <div className={styles.loadingState}>
          <p>Loading affiliate information...</p>
        </div>
      </div>
    );
  }

  // No affiliate record - show application button
  if (!affiliateStatus?.affiliate) {
    return (
      <div className={styles.affiliateTab}>
        <div className={styles.affiliateIntro}>
          <div className={styles.affiliateIntroContent}>
          <h4 className={styles.subsectionTitle}>Become an Affiliate</h4>
          <p className={styles.affiliateDescription}>
            Join our affiliate program and earn commission on referrals. Create
            custom coupon codes and track your earnings through our
            comprehensive dashboard.
          </p>
          </div>
          </div>

          <div className={styles.affiliateBenefits}>
            <h5>Benefits:</h5>
            <ul>
              <li>Earn up to 10% commission on successful referrals</li>
              <li>Create custom discount codes for your audience</li>
              <li>Access detailed analytics and earnings reports</li>
              <li>Monthly payouts to your preferred payment method</li>
            </ul>
          </div>

          <Button
            onClick={() => setShowApplicationForm(true)}
            className={styles.primaryButton}
          >
            Apply to Become an Affiliate
          </Button>
        

        {/* Application Form Modal */}
        <AffiliateApplicationForm
          isOpen={showApplicationForm}
          onClose={() => setShowApplicationForm(false)}
          onSubmit={handleSubmitApplication}
          isSubmitting={submitting}
        />
      </div>
    );
  }

  const affiliate = affiliateStatus.affiliate;
  const summary = affiliateStatus.commission_summary;

  // Show affiliate dashboard for existing affiliates
  return (
    <div className={styles.affiliateTab}>
      {/* Status Section */}
      <div className={styles.affiliateStatusSection}>
        <h4 className={styles.subsectionTitle}>Affiliate Status</h4>
        <div className={styles.statusContainer}>
          <div
            className={styles.statusBadge}
            style={{ backgroundColor: getStatusColor(affiliate.status) }}
          >
            {getStatusText(affiliate.status)}
          </div>
          <div className={styles.statusDetails}>
            <p>
              Applied: {new Date(affiliate.created_at).toLocaleDateString()}
            </p>
            {affiliate.approved_at && (
              <p>
                Approved: {new Date(affiliate.approved_at).toLocaleDateString()}
              </p>
            )}
            <p>Commission Rate: {affiliate.default_commission_rate}%</p>
          </div>
        </div>
      </div>

      {/* Pending Status Message */}
      {affiliate.status === "pending" && (
        <div className={styles.pendingMessage}>
          <h4>Application Under Review</h4>
          <p>
            Your affiliate application is currently being reviewed by our team.
            We'll notify you once a decision has been made. This process
            typically takes 3-5 business days.
          </p>
        </div>
      )}

      {/* Rejected Status Message */}
      {affiliate.status === "rejected" && (
        <div className={styles.rejectedMessage}>
          <h4>Application Not Approved</h4>
          <p>
            Unfortunately, your affiliate application was not approved at this
            time.
            {affiliate.notes && (
              <>
                <br />
                <strong>Reason:</strong> {affiliate.notes}
              </>
            )}
          </p>
          <p>You may reapply after addressing any concerns mentioned above.</p>
        </div>
      )}

      {/* Active Affiliate Dashboard */}
      {affiliate.status === "active" && (
        <>
          {/* Affiliate Code Section */}
          {affiliateStatus.affiliate_coupon && (
            <div className={styles.affiliateCodeSection}>
              <h4 className={styles.subsectionTitle}>Your Affiliate Code</h4>
              <div className={styles.referralCodeContainer}>
                <div className={styles.referralCodeDisplay}>
                  <span className={styles.referralCode}>
                    {affiliateStatus.affiliate_coupon.code}
                  </span>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        affiliateStatus.affiliate_coupon!.code
                      );
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    }}
                    className={styles.copyButton}
                    title="Copy affiliate code"
                  >
                    {copySuccess ? "✓" : "📋"}
                  </button>
                </div>
                <p className={styles.referralCodeDescription}>
                  Share this code to grant discounts and earn commission.
                </p>
              </div>
            </div>
          )}

          {/* Earnings Summary */}
          {summary && (
            <div className={styles.earningsSection}>
              <h4 className={styles.subsectionTitle}>Earnings Summary</h4>
              <div className={styles.earningsGrid}>
                <div className={styles.earningsCard}>
                  <div className={styles.earningsAmount}>
                    {formatAmount(summary.available_commission_minor)}
                  </div>
                  <div className={styles.earningsLabel}>
                    Available for Withdrawal
                  </div>
                </div>
                <div className={styles.earningsCard}>
                  <div className={styles.earningsAmount}>
                    {formatAmount(summary.pending_commission_minor)}
                  </div>
                  <div className={styles.earningsLabel}>
                    Pending (Hold Period)
                  </div>
                </div>
                <div className={styles.earningsCard}>
                  <div className={styles.earningsAmount}>
                    {formatAmount(summary.paid_commission_minor)}
                  </div>
                  <div className={styles.earningsLabel}>Total Paid</div>
                </div>
              </div>
              <div className={styles.payoutAction}>
                <Button
                  onClick={handleRequestPayout}
                  className={styles.primaryButton}
                  disabled={
                    requestingPayout ||
                    !summary ||
                    summary.available_commission_minor < 5000 || // 50 SAR minimum
                    summary.processing_commission_minor > 0
                  }
                >
                  {requestingPayout
                    ? "Requesting..."
                    : summary.processing_commission_minor > 0
                    ? "Payout in Progress"
                    : "Request Payout (min 50 SAR)"}
                </Button>
              </div>
            </div>
          )}

          {/* Commission Details */}
          {summary && (
            <div className={styles.commissionDetails}>
              <h4 className={styles.subsectionTitle}>Commission Details</h4>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Commission Rate:</span>
                  <span className={styles.detailValue}>
                    {affiliate.default_commission_rate}%
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Processing:</span>
                  <span className={styles.detailValue}>
                    {formatAmount(summary.processing_commission_minor)} SAR
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Total Eligible:</span>
                  <span className={styles.detailValue}>
                    {formatAmount(summary.eligible_commission_minor)} SAR
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Payout History */}
          {affiliateStatus.payouts && affiliateStatus.payouts.length > 0 && (
            <div className={styles.payoutHistory}>
              <h4 className={styles.subsectionTitle}>Payout History</h4>
              <ul className={styles.payoutList}>
                {affiliateStatus.payouts.map((payout) => (
                  <li key={payout.id} className={styles.payoutItem}>
                    <div className={styles.payoutItemHeader}>
                      <span className={styles.payoutAmount}>
                        {formatAmount(payout.total_commission_minor)} SAR
                      </span>
                      <span
                        className={`${styles.payoutStatus} ${
                          styles[payout.status]
                        }`}
                      >
                        {payout.status}
                      </span>
                    </div>
                    <div className={styles.payoutItemDetails}>
                      <p>
                        Requested:{" "}
                        {new Date(payout.created_at).toLocaleDateString()}
                      </p>
                      {payout.paid_at && (
                        <p>
                          Paid: {new Date(payout.paid_at).toLocaleDateString()}
                        </p>
                      )}
                      {payout.payment_reference && (
                        <p>Reference: {payout.payment_reference}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Notes Section */}
      {affiliate.notes && (
        <div className={styles.notesSection}>
          <h4 className={styles.subsectionTitle}>Notes</h4>
          <p className={styles.notesText}>{affiliate.notes}</p>
        </div>
      )}

      {/* Copy Success Message */}
      {copySuccess && (
        <div className={styles.copySuccessMessage}>Copied to clipboard! ✓</div>
      )}
    </div>
  );
};

export default AffiliateTab;



